/**
 * @file PanoramaBackground.ts
 *
 * @description Shows a photo sphere's equirectangular panorama behind the
 * point cloud by sampling the photo directly. It replaced Skybox, which first
 * converted the photo into a six-sided cube map: the result looks the same
 * (checked pixel by pixel), but that conversion took ~0.3 s and ~0.4 GB of GPU
 * memory more for an 8192 x 4096 photo.
 *
 * The photo is lined up with the point cloud with the rotations Skybox gave
 * its cube camera, applied to the direction each pixel looks in.
 */

import {
    BackSide,
    BoxGeometry,
    LinearFilter,
    Matrix3,
    Mesh,
    Object3D,
    ShaderMaterial,
    Texture,
    Vector3,
} from 'three';

const vertexShader = /* glsl */`
    varying vec3 vWorldDirection;
    void main() {
        // The box sits on the camera: only the view's rotation matters, and
        // xyww puts it at the far plane, behind everything else.
        vWorldDirection = position;
        vec4 clip = projectionMatrix * vec4(mat3(viewMatrix) * position, 1.0);
        gl_Position = clip.xyww;
    }
`;

const fragmentShader = /* glsl */`
    uniform sampler2D tEquirect;
    uniform mat3 uRotation;
    varying vec3 vWorldDirection;
    #include <common>
    void main() {
        vec3 direction = normalize(uRotation * vWorldDirection);
        gl_FragColor = texture2D(tEquirect, equirectUv(direction));
    }
`;

/**
 * @description The rotation Skybox gave its cube camera for a photo sphere's
 * orientation, as a matrix applied to view directions.
 * @param rotation The photo sphere's initial rotation
 * @return {Matrix3} The rotation
 */
export const panoramaRotation = (rotation: Vector3): Matrix3 => {
    // The steps, in order, that Skybox applied to its cube camera.
    const orientation = new Object3D();
    orientation.up.set(0, 0, 1);
    orientation.rotateOnAxis(new Vector3(0, 1, 0), -Math.PI / 2);
    orientation.rotateZ((rotation.z / 200) * -Math.PI);
    orientation.rotateY((rotation.y / 200) * -Math.PI);
    orientation.rotateX((rotation.x / 200) * -Math.PI);
    orientation.updateMatrixWorld();
    return new Matrix3().setFromMatrix4(orientation.matrixWorld);
};

class PanoramaBackground {
    public readonly mesh: Mesh;
    private readonly material: ShaderMaterial;
    private readonly texture: Texture;

    /**
     * @param texture The loaded equirectangular photo
     * @param rotation The photo sphere's initial rotation
     */
    constructor(texture: Texture, rotation: Vector3) {
        // No mipmaps: they would add a third more memory, and equirectangular
        // sampling picks the wrong level along the photo's left/right seam.
        texture.generateMipmaps = false;
        texture.minFilter = LinearFilter;
        texture.magFilter = LinearFilter;
        texture.needsUpdate = true;
        this.texture = texture;

        this.material = new ShaderMaterial({
            name: 'PanoramaBackground',
            uniforms: {
                tEquirect: { value: texture },
                uRotation: { value: panoramaRotation(rotation) },
            },
            vertexShader,
            fragmentShader,
            side: BackSide,
            depthTest: false,
            depthWrite: false,
        });

        this.mesh = new Mesh(new BoxGeometry(2, 2, 2), this.material);
        // Drawn first, wherever the camera is.
        this.mesh.renderOrder = -Infinity;
        this.mesh.frustumCulled = false;
    }

    dispose() {
        this.mesh.removeFromParent();
        this.mesh.geometry.dispose();
        this.material.dispose();
        this.texture.dispose();
    }
}

export { PanoramaBackground };
